import Button from 'components/ui/Button/Button';
import Tabs from 'components/ui/Tabs/Tabs';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import Rating from 'components/ui/Rating/Rating';
import { Select } from 'ts/interfaces/Generic';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { useTranslation } from 'react-i18next';
import {
  getHotelDisplayModeSettings,
  hotelCardStyles,
  roomModes,
  searchTypeIds,
} from 'constants/constant';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faGlobe, faStar, faTimes } from '@fortawesome/free-solid-svg-icons';
import { showError, showSuccess } from 'utils/utils';
import { getSerializeAndCompressJson, saveUserConfig } from 'services/user';
import useRecaptcha from 'hooks/useRecaptcha';
import ScreenshotSupport from './ScreenshotSupport';
import withErrorBoundary from './withErrorBoundary';
import { getSearchFilter } from 'services/cms';
import useDraggableModal from 'hooks/useDraggableModal';
import { createPortal } from 'react-dom';
import MaximizeModal from './MaximizeModal';
import DisplayModeConfig from './DisplayModeConfig';
import { MainContext } from 'contexts/MainContext';
import { hotelConfigImage, fl1Img, fl2Img, mo1Img, mo2Img, defaultSearchTypeId } from 'constants/generic';

const ConfigModal = () => {
  const {
    t } = useTranslation();
  const { modalRef,
    modalStyle,
    dragHandleProps,
    isDragging } = useDraggableModal(true);
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const [showViewConfig,
    setShowViewConfig] = useState(false);
  const { userSiteConfig, setUserSiteConfig, initSiteData } = useContext(MainContext);
  const [activeTab, setActiveTab] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const { handleRecapcha } = useRecaptcha();

  const [starRating, setStarRating] = useState<Select[]>([]);
  const [configState, setConfigState] = useState(userSiteConfig);
  const [flightsAvailable, setFlightsAvailable] = useState(false);

  useEffect(() => {
    setConfigState((prev: any) => {
      return {
        ...prev,
      };
    });

    if (initSiteData?.other?.flightsAvailbale) {
      setFlightsAvailable(true);
    }
  }, [initSiteData]);

  useEffect(() => {
    if (showViewConfig) {
      (async () => {
        const newFilter = await getSearchFilter();

        const newRatings =
          newFilter?.starRatingList.map((item: any) => ({
            value: parseFloat(item.value),
            label: item.text,
            html: (
              <div className="display-flex">
                <Rating value={item.value} className="mr-10" />
                {item.text}
              </div>
            ),
          })) || [];
        setStarRating(newRatings);
        setConfigState((prev: any) => {
          const newStarRating =
            (prev.hotel.starRating ? parseFloat(prev.hotel.starRating) : null) ??
            newFilter?.starRating ??
            3;
          return {
            ...prev,
            hotel: {
              ...prev.hotel,
              starRating: newStarRating,
            },
          };
        });
      })();
    }
  }, [showViewConfig]); // eslint-disable-line

  const onChangeDisplayMode = (value: number) => {
    const settings = getHotelDisplayModeSettings(value);
    if (!settings) {
      return;
    }

    setConfigState({
      ...configState,
      hotel: {
        ...configState.hotel,
        ...settings,
      },
    });
  };

  const onChangeHotelCardStyle = (value: number) => {
    setConfigState({
      ...configState,
      hotel: {
        ...configState.hotel,
        hotelCardStyle: value,
      },
    });
  };

  const onChangeRoomMode = (value: number) => {
    switch (value) {
      case 0:
        setConfigState({
          ...configState,
          hotel: {
            ...configState.hotel,
            roomMode: value,
            showRoomsInTab: false,
          },
        });
        break;
      case 2:
        setConfigState({
          ...configState,
          hotel: {
            ...configState.hotel,
            roomMode: value,
            showRoomsInTab: true,
          },
        });
        break;
    }
  };

  const saveConfig = async () => {
    setIsLoading(true);
    const recaptchaToken = await handleRecapcha();

    const res = await saveUserConfig({
      recaptchaToken,
      input: configState,
    });

    if (res?.success) {
      const userLocal = JSON.parse(localStorage.getItem('user') || '{}');
      userLocal.config = configState;
      setUserSiteConfig({ ...configState, hotel: { ...configState.hotel, displayStyle: '2' } });
      localStorage.setItem('user', JSON.stringify(userLocal));
      showSuccess(t('hotel.config_saved'));
    } else {
      showError(t('something_went_wrong'));
    }

    setIsLoading(false);
  };

  const getConfigJson = async () => {
    setIsLoading(true);

    const configStr = {
      base: JSON.stringify(configState.base),
      hotel: JSON.stringify(configState.hotel),
    };

    const res = await getSerializeAndCompressJson({ input: configStr });

    if (res) {
      navigator.clipboard.writeText(res);
      showSuccess(t('hotel.serialized_config_clipboard'));
    } else {
      showError(t('something_went_wrong'));
    }

    setIsLoading(false);
  };

  const close = () => {
    setShowViewConfig(false);
    setConfigState(userSiteConfig);
  };

  const getImg = () => {
    const { displayMode } = configState.hotel;

    if (!displayMode || displayMode === 7) {
      return hotelConfigImage.n1;
    } else if (displayMode === 8) {
      return hotelConfigImage.n2;
    } else if (displayMode === 9) {
      return hotelConfigImage.n3;
    } else if (displayMode === 10) {
      return hotelConfigImage.n4;
    } else if (displayMode === 11) {
      return hotelConfigImage.n5;
    } else if (displayMode === 12) {
      return hotelConfigImage.n6;
    } else if (displayMode === 13) {
      return hotelConfigImage.n7;
    } else if (displayMode === 14) {
      return hotelConfigImage.n8;
    } else {
      return hotelConfigImage.n2;
    }
  };

  return (
    <Fragment>
      <FontAwesomeIcon
        className="s-history-icon"
        icon={faGear}
        onClick={() => {
          setConfigState(userSiteConfig);
          setShowViewConfig(true);
        }}
      />
      {showViewConfig && (
        <Fragment>
          {createPortal(
            <div className="modal-overlay modal-overlay-top modal-maximizable" ref={modalOverlayRef}>
              <div ref={modalRef} className="modal active" style={modalStyle}>
                <div
                  className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
                  {...dragHandleProps}
                >
                  <div className="title">{t('hotel.display_settings')}</div>
                  <div data-no-drag="true">
                    <ScreenshotSupport />
                    <MaximizeModal modalOverlayRef={modalOverlayRef} />
                    <FontAwesomeIcon icon={faTimes} onClick={close} />
                  </div>
                </div>
                <div className="modal-content">
                  <Tabs
                    label={t('hotel.display_settings')}
                    value={String(activeTab)}
                    onValueChange={(value) => setActiveTab(Number(value))}
                    items={[
                      {
                        value: '1',
                        label: <span className="mw-200">General</span>,
                      },
                      {
                        value: '0',
                        label: <span className="mw-200">{t('hotel.hotel')}</span>,
                      },
                      {
                        value: '2',
                        label: <span className="mw-200">{t('flight.flight')}</span>,
                        disabled: !flightsAvailable,
                      },
                      {
                        value: '3',
                        label: <span className="mw-200">{t('mo.mo')}</span>,
                      },
                    ]}
                  />

                  {activeTab === 2 && (
                    <div className="hotel-config flex-column">
                      <div className="flex-between hotel-config">
                        <div className="hotel-config-list w100p">
                          <div className="mb-5">{t('hotel.display_mode')}</div>
                          <SelectCustom
                            options={[
                              { value: 'false', label: t('hotel.client') },
                              { value: 'true', label: t('hotel.bussiness') },
                            ]}
                            value={configState.base.professionalMode?.toString() || 'false'}
                            onChange={(mode) =>
                              setConfigState({
                                ...configState,
                                base: {
                                  ...configState.base,
                                  professionalMode: mode === 'true',
                                },
                              })
                            }
                          />
                        </div>
                        <div className="hotel-config-preview">
                          {configState.base.professionalMode ? (
                            <img src={fl1Img} alt="" />
                          ) : (
                            <img src={fl2Img} alt="" />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === 1 && (
                    <div className="hotel-config flex-column">
                      <Checkbox
                        className="mb-10"
                        checked={configState.base.openDetailsInNewTab}
                        onChange={() =>
                          setConfigState({
                            ...configState,
                            base: {
                              ...configState.base,
                              openDetailsInNewTab: !configState.base.openDetailsInNewTab,
                            },
                          })
                        }
                        label={t('search.open_details_in_new_tab')}
                      />
                      <Checkbox
                        className="mb-10"
                        checked={configState.base.rememberModeEnabled}
                        onChange={() =>
                          setConfigState({
                            ...configState,
                            base: {
                              ...configState.base,
                              rememberModeEnabled: !configState.base.rememberModeEnabled,
                            },
                          })
                        }
                        label={t('search.remember_mode_enabled')}
                      />
                    </div>
                  )}
                  {activeTab === 0 && (
                    <div className="flex-between hotel-config">
                      <div className="hotel-config-list">
                        <div className="mb-20 hotel-config-list-mode">
                          <DisplayModeConfig
                            style={{ marginInlineEnd: -40 }}
                            value={configState.hotel.displayMode || 7}
                            onChange={onChangeDisplayMode}
                          />
                        </div>
                        {/* <div className="mb-20">
                          <div className="mb-5">{t('hotel.display_style')}</div>
                          <SelectCustom
                            options={displayStyles}
                            value={configState.hotel.displayStyle || '1'}
                            onChange={(val) => onChangeDisplayStyle(val)}
                            needToTranslate={true}
                          />
                        </div> */}
                        <div className="mb-20">
                          <div className="mb-5">{t('hotel.room_display')}</div>
                          <SelectCustom
                            options={roomModes}
                            value={configState.hotel.roomMode}
                            onChange={onChangeRoomMode}
                            needToTranslate={true}
                          />
                        </div>
                        <div className="mb-20">
                          <div className="mb-5">Active Hotel Style</div>
                          <SelectCustom
                            options={hotelCardStyles}
                            value={configState.hotel.hotelCardStyle || 0}
                            onChange={onChangeHotelCardStyle}
                            needToTranslate={true}
                          />
                        </div>
                        <Checkbox
                          className="mb-5"
                          checked={configState.hotel.areRoomsGrouped}
                          onChange={() =>
                            setConfigState({
                              ...configState,
                              hotel: {
                                ...configState.hotel,
                                areRoomsGrouped: !configState.hotel.areRoomsGrouped,
                              },
                            })
                          }
                          label={t('hotel.group_rooms')}
                        />
                        <Checkbox
                          className="mb-5"
                          checked={configState.hotel.isRoomNameCleared}
                          onChange={() =>
                            setConfigState({
                              ...configState,
                              hotel: {
                                ...configState.hotel,
                                isRoomNameCleared: !configState.hotel.isRoomNameCleared,
                              },
                            })
                          }
                          label={t('hotel.clear_room_name')}
                        />
                        {/* <Checkbox
                          className="mb-5"
                          checked={configState.hotel.areRoomsFiltered}
                          onChange={() =>
                            setConfigState({
                              ...configState,
                              hotel: {
                                ...configState.hotel,
                                areRoomsFiltered: !configState.hotel.areRoomsFiltered,
                              },
                            })
                          }
                          label={t('hotel.filter_rooms')}
                        /> */}
                        <Checkbox
                          className="mb-5"
                          checked={configState.hotel.oneHotelSearchByOneSupplier}
                          onChange={() =>
                            setConfigState({
                              ...configState,
                              hotel: {
                                ...configState.hotel,
                                oneHotelSearchByOneSupplier:
                                  !configState.hotel.oneHotelSearchByOneSupplier,
                              },
                            })
                          }
                          label="One Hotel - Current Supplier only"
                        />

                        <div className="mt-20">
                          <SelectCustom
                            options={searchTypeIds}
                            value={configState.hotel.searchTypeId || defaultSearchTypeId}
                            onChange={(searchTypeId) =>
                              setConfigState({
                                ...configState,
                                hotel: {
                                  ...configState.hotel,
                                  searchTypeId,
                                },
                              })
                            }
                            movablePlaceholder={t('search.search_type_id')}
                            movablePlaceholderIcon={faGlobe}
                            menuPlacement="top"
                          />
                        </div>
                        <div className="mt-20">
                          <SelectCustom
                            options={starRating}
                            value={configState.hotel.starRating}
                            onChange={(starRating) =>
                              setConfigState({
                                ...configState,
                                hotel: {
                                  ...configState.hotel,
                                  starRating,
                                },
                              })
                            }
                            movablePlaceholder={t('hotel.stars')}
                            movablePlaceholderIcon={faStar}
                            movablePlaceholderAlwaysSmall={true}
                            menuPlacement="top"
                          />
                        </div>
                      </div>
                      <div className="hotel-config-preview">
                        <img className="img" src={getImg()} alt="config" />
                      </div>
                    </div>
                  )}
                  {activeTab === 3 && (
                    <div className="hotel-config flex-column">
                      <div className="flex-between hotel-config">
                        <div className="hotel-config-list w100p">
                          <div className="mb-5">{t('hotel.display_mode')}</div>
                          <SelectCustom
                            options={[
                              { value: '0', label: 'Default' },
                              { value: '1', label: 'Client Mode' },
                            ]}
                            value={configState.base.myOrdersMode?.toString() || '1'}
                            onChange={(mode) =>
                              setConfigState({
                                ...configState,
                                base: {
                                  ...configState.base,
                                  myOrdersMode: parseInt(mode),
                                },
                              })
                            }
                          />
                        </div>

                        <div className="hotel-config-preview">
                          {configState.base.myOrdersMode === 0 ? (
                            <img src={mo1Img} alt="" />
                          ) : (
                            <img src={mo2Img} alt="" />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <Button
                    className="btn btn-primary ml-0"
                    title={t('save')}
                    onClick={saveConfig}
                    disabled={isLoading}
                  />
                  <Button
                    className="btn btn-primary mr-a"
                    title={t('hotel.get_serialiezed_config')}
                    onClick={getConfigJson}
                    disabled={isLoading}
                  />
                  <Button className="btn btn-default" title={t('close')} onClick={close} />
                </div>
              </div>
            </div>,
            document.body
          )}
        </Fragment>
      )}
    </Fragment>
  );
};

export default withErrorBoundary(ConfigModal, 'ConfigModal');
